import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import LoadingPage from '../LoadingPage';
import { getColors } from '../../Constants';
import './SvgChart.css';
import PartyList from './PartyList';

interface MandatData {
    pocet_mandatu: number;
    strana_barva: string;
    strana_id: number;
    strana_zkratka?: string;
}

type SvgChartProps = {
    basicData: {
        nejnovejsi_zasedani?: {
            mandaty: Array<{
                pocet_mandatu: number;
                strana_barva: string;
                strana_id: number;
            }>;
            legalni_mandaty: Array<{
                pocet_mandatu: number;
                strana_barva: string;
                strana_id: number;
            }>;
        };
        koalice: Array<{ strana_zkratka: string }>;
    };
    partiesData: any[];
    legalPartiesData: any[];
    coalitionView?: boolean;
    epView?: boolean;
    politicalView: string;
};

const calculateRowsMax = (itemCount: number) : number => {
    if (itemCount <= 30) {
        return 3;
    } else if (itemCount <= 60) {
        return 4;
    } else if (itemCount <= 200) {
        return 5;
    } else if (itemCount <= 400) {
        return 7;
    } else {
        // At this size (in practice just the European Parliament's ~700 seats), dot size is
        // capped by columns (itemCount / rows) far more than by available width - the fewer
        // columns, the bigger each dot can be for the same row width. 20 rows keeps the grid
        // closer to a landscape rectangle (~36 columns for ~720 seats) instead of the very wide,
        // very flat strip fewer rows produce, trading chart height for legible dots.
        return 20;
    }
};

const SvgChart = ({ basicData, partiesData, legalPartiesData, coalitionView, epView, politicalView }: SvgChartProps) => {
    const navigate = useNavigate();
    const colors = getColors();

    const itemCount = useMemo(() => {
        return (basicData.nejnovejsi_zasedani?.mandaty || [])
            .reduce((sum, item) => sum + item.pocet_mandatu, 0);
    }, [basicData]);

    const [rowsMax, setRowsMax] = useState<number>(calculateRowsMax(itemCount));

    useEffect(() => {
        const handleResize = () => setRowsMax(calculateRowsMax(itemCount));

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [itemCount]);

    // Dots are sized off the chart's own rendered width rather than the window width, so the
    // chart fills the space it actually has inside its card instead of leaving it around a
    // dot cluster sized for the full viewport.
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(
        typeof window !== 'undefined' ? window.innerWidth : 0
    );

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setContainerWidth(entry.contentRect.width);
        });
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const generateSvg = () => {
        const zasedani = basicData?.nejnovejsi_zasedani;
        if (!zasedani) return "";

        // Color link
        const memberOriginalColorMap: Record<number, string> = {};
        legalPartiesData.forEach(party => {
            party.clenove.forEach((member: any) => {
                if (member.aktivni === 1) {
                    memberOriginalColorMap[member.id] = party.strana_barva;
                }
            });
        });
        const clubMemberColorsMap: Record<number, string[]> = {};
        partiesData.forEach(club => {
            const sortedActiveMembers = club.clenove
                .filter((m: any) => m.aktivni === 1)
                .sort((a: any, b: any) => {
                    const colorA = memberOriginalColorMap[a.id] || "";
                    const colorB = memberOriginalColorMap[b.id] || "";
                    return colorA.localeCompare(colorB);
                });
    
            clubMemberColorsMap[club.strana_id] = sortedActiveMembers.map(
                (m: any) => memberOriginalColorMap[m.id] || club.strana_barva
            );
        });

        // Type of political view
        let mandaty: MandatData[] = [];
        if (politicalView === 'parties') {
            mandaty = zasedani.legalni_mandaty || [];
        } else if (politicalView === 'subjects' || politicalView === 'combined') {
            mandaty = zasedani.mandaty || [];
        }

        // Calculate parameters
        const rows = itemCount > rowsMax ? rowsMax : itemCount;
        const columns = Math.ceil(itemCount / rowsMax);

        const max = 20;
        const effectiveWidth = containerWidth || window.innerWidth;
        // The /6 divisor (kept for the large-council/narrow-viewport case) was tuned back when
        // this sized off the full window width; now that it sizes off the chart's own container,
        // that divisor left dots far smaller than the card had room for - independent of council
        // size, since columns cancels out of the fill-fraction (total width / effectiveWidth).
        // /2.4 targets ~97% of the container's width (solving fill ≈ 7 / (3 * divisor) for the
        // uncapped case), instead of the ~55% the old /6 left it at.
        const calculated_radius = itemCount >= 200 && window.innerWidth <= 750 ? effectiveWidth / columns / 3 : effectiveWidth / columns / 2.4;
        const radius = calculated_radius > max ? max : calculated_radius;
        const gap = calculated_radius / 3 > max / 3 ? max / 3 : calculated_radius / 3;
        const offset = radius * 2 + gap;

        // First dot starts with a gap, and then it needs 1 radius of space, so it does not clip
        let x = gap + radius;
        let y = gap + radius;

        const width = gap + offset * columns;
        const height = gap + offset * rows;

        let content = `<svg viewbox="0 0 ${width} ${height}" height="${height}" xmlns="http://www.w3.org/2000/svg">`;

        let currentIdx = 0;
        const groupLimits = mandaty.map((party) => {
            currentIdx = currentIdx + party.pocet_mandatu;
            return currentIdx;
        });

        let partyIdx = 0;
        let memberInPartyIdx = 0;
    
        for (let i = 0; i < itemCount; i++) {
            if (i === 0) content += `<g>`;
            
            if (i > 0 && groupLimits.includes(i)) {
                content += `</g><g>`;
                partyIdx++;
                memberInPartyIdx = 0;
            }
    
            if (i > 0 && i % rowsMax === 0) {
                x = x + offset;
                y = gap + radius; 
            }

            const currentGroup = mandaty[partyIdx];
            const partyId = currentGroup?.strana_id;
            const baseColor = currentGroup?.strana_barva || 'black';

            // Party dont have page
            const isClickable = politicalView === 'subjects' || politicalView === 'combined';
            const cursorStyle = isClickable ? 'cursor:pointer' : 'cursor:default';
            
            if (politicalView === 'combined' && currentGroup) {
                const memberSatColors = clubMemberColorsMap[currentGroup.strana_id];
                const satColor = (memberSatColors && memberSatColors[memberInPartyIdx]) 
                                    ? memberSatColors[memberInPartyIdx] 
                                    : baseColor;

                const mainR = radius * 0.85;
                const satR = mainR * 0.5; // Sattelite radius
                
                const rShift = Math.cos(Math.PI / 4) * (mainR + satR / 4);

                const satX = x + rShift;
                const satY = y + rShift;
            
                content += `<g class="party-${partyId}" style="${cursorStyle}">`;
                
                content += `<ellipse 
                    class="party-${partyId}" 
                    ry="${mainR}" rx="${mainR}" 
                    cy="${y}" cx="${x}" 
                    fill="${baseColor}" 
                    style="${cursorStyle}"
                />`;
            
                content += `<ellipse 
                    class="party-${partyId}-satellite" 
                    ry="${satR}" rx="${satR}" 
                    cy="${satY}" cx="${satX}" 
                    fill="${satColor}" 
                    stroke="white"
                    stroke-width="1.5"
                    style="${cursorStyle}"
                />`;
                
                content += `</g>`;
            } else {
                content += `<ellipse 
                    class="party-${partyId}" 
                    ry="${radius}" rx="${radius}" 
                    cy="${y}" cx="${x}" 
                    fill="${baseColor}" 
                    style="${cursorStyle}"
                />`;
            }
    
            y = y + offset;
            memberInPartyIdx++;
        }
        
        content += '</g></svg>';
        const base64 = btoa(unescape(encodeURIComponent(content)));
        return `data:image/svg+xml;base64,${base64}`;
    };

    const svgData = generateSvg();

    const [hoveredParties, setHoveredParties] = useState<(string | number)[] | null>(null);
    const svgRef = useRef<SVGElement>(null);

    const handleMouseEnter = useCallback((partyId: string) => {
        let idsToHover: (string | number)[] = [partyId];

        // For combined view find all legal parties associated with this subject
        if (politicalView === 'combined') {
            const club = partiesData.find(p => String(p.strana_id) === String(partyId));
            if (club && club.clenove) {
                const clubMemberIds = new Set(club.clenove.map((m: any) => m.id));

                legalPartiesData.forEach(lp => {
                    if (lp.clenove && lp.clenove.some((m: any) => clubMemberIds.has(m.id))) {
                        idsToHover.push(lp.strana_id);
                    }
                });
            }
        }

        setHoveredParties(idsToHover);
    }, [politicalView, partiesData, legalPartiesData]);
    
    const handleMouseLeave = useCallback(() => {
        setHoveredParties(null);
    }, []);
    
    const handleEllipseClick = useCallback((partyId: string) => {
        if (politicalView === 'parties') return;
        
        navigate(`subjekty/${partyId}`);
    }, [navigate, politicalView]);

    const attachEventListeners = useCallback((svgElement: SVGElement | null) => {
        if (!svgElement) return;
        
        const elements = svgElement.querySelectorAll<SVGElement>('[class^="party-"]');
    
        elements.forEach((el) => {
            const node = el as any;
            const className = node.getAttribute('class');
            if (!className) return;
            
            const partyId = className.split(' ').find((c: string) => c.startsWith('party-'))?.split('-')[1];
            if (!partyId) return;
            
            const mouseenter = () => handleMouseEnter(partyId);
            const mouseleave = () => handleMouseLeave();
            const click = () => handleEllipseClick(partyId);
    
            node.addEventListener('mouseenter', mouseenter);
            node.addEventListener('mouseleave', mouseleave);
            node.addEventListener('click', click);
    
            node._eventListeners = { mouseenter, mouseleave, click };
        });
    
        // Cleanup funkce
        return () => {
            elements.forEach((el) => {
                const node = el as any;
                if (node._eventListeners) {
                    const { mouseenter, mouseleave, click } = node._eventListeners;
                    node.removeEventListener('mouseenter', mouseenter);
                    node.removeEventListener('mouseleave', mouseleave);
                    node.removeEventListener('click', click);
                    delete node._eventListeners;
                }
            });
        };
    }, [handleMouseEnter, handleMouseLeave, handleEllipseClick]);

    // Update table hover
    useEffect(() => {
        let cleanupFn: (() => void) | undefined;
        const timeoutId = setTimeout(() => {
            if (svgRef.current) {
                cleanupFn = attachEventListeners(svgRef.current);
            }
        }, 100);
    
        return () => {
            clearTimeout(timeoutId);
            if (cleanupFn) cleanupFn();
        };
    }, [svgData, attachEventListeners]);

     const { koalice } = basicData;

    // Data separation
    const getSplitData = (sourceArray: any[]) => {
        const active = sourceArray.filter((p) => p.aktivni);
        
        return {
            coalition: active.filter((p) => 
                koalice.some((c: any) => c.strana_zkratka === p.strana_zkratka)
            ),
            opposition: active.filter((p) => 
                !koalice.some((c: any) => c.strana_zkratka === p.strana_zkratka)
            )
        };
    };

    return (
        <div className='mandate-svg'>
            <div className='item-svg' ref={containerRef}>
                <SVG
                    innerRef={svgRef}
                    loader={<LoadingPage />}
                    src={svgData}
                />
            </div>

            <div className="party-lists-container">
                {politicalView === 'combined' ? (
                    <>
                        <div className="combined-lists-wrapper">
                            <div className="list-section">
                                <PartyList
                                    coalitionParties={getSplitData(partiesData).coalition}
                                    oppositionParties={getSplitData(partiesData).opposition}
                                    epText={epView}
                                    splitByCoalition={false}
                                    hoveredParties={hoveredParties}
                                    isSubject={true}
                                    colors={colors}
                                    twoLists={true}
                                />
                            </div>
                            
                            <div className="list-section">
                                <PartyList
                                    coalitionParties={getSplitData(legalPartiesData).coalition}
                                    oppositionParties={getSplitData(legalPartiesData).opposition}
                                    epText={epView}
                                    splitByCoalition={false}
                                    hoveredParties={hoveredParties}
                                    isSubject={false}
                                    colors={colors}
                                    twoLists={true}
                                />
                            </div>
                        </div>
                    </>
                ) : (

                    <PartyList
                        coalitionParties={getSplitData(politicalView === 'parties' ? legalPartiesData : partiesData).coalition}
                        oppositionParties={getSplitData(politicalView === 'parties' ? legalPartiesData : partiesData).opposition}
                        epText={epView}
                        splitByCoalition={coalitionView}
                        hoveredParties={hoveredParties}
                        isSubject={politicalView === 'parties' ? false : true}
                        colors={colors}
                    />
                )}
            </div>
        </div>
    );
}
export default SvgChart;
